'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Loading state shown while an estimate is pending/generating.
 *
 * Instead of a bare spinner, we narrate the calculation the server is actually
 * doing (read photos -> measure areas -> match materials -> price the work).
 * This turns the ~1 minute wait into an explanation of where the final number
 * comes from, which reinforces trust in the estimate.
 *
 * Timing is indeterminate: we have no real per-step progress from the API, so
 * steps advance on a timer and the LAST step intentionally never completes — it
 * stays "active" until the real estimate arrives and this component unmounts.
 * That keeps the UI honest (no fake 100%).
 */

const STEP_KEYS = [
  'generatingStepDesign',
  'generatingStepAreas',
  'generatingStepMaterials',
  'generatingStepMarket',
  'generatingStepLocal',
  'generatingStepPricing',
] as const;

// Per-step dwell time before advancing. Front-loaded fast (real generation is
// ~30s), then slowing down — so the early steps clearly progress and the last
// step ("Pricing the work") is usually still active when the estimate lands.
// The final entry is unused (we stall on the last step), kept for symmetry.
const STEP_DELAYS_MS = [3_000, 3_000, 3_750, 4_500, 6_000, 0];

export function EstimateGeneratingState() {
  const t = useTranslations('common.estimate.editor');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let timer: ReturnType<typeof setTimeout>;
    const schedule = (index: number) => {
      // Stall on the last step — we never claim completion we can't verify.
      if (index >= STEP_KEYS.length - 1) return;
      timer = setTimeout(() => {
        const next = index + 1;
        setActiveIndex(next);
        schedule(next);
      }, STEP_DELAYS_MS[index]);
    };
    schedule(0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 py-16">
      <div className="text-center">
        <p className="text-list-item-title text-foreground">{t('generating')}</p>
        <p className="mt-1 text-card-meta text-muted-foreground">
          {t('generatingHint')}
        </p>
      </div>

      <ol className="flex flex-col">
        {STEP_KEYS.map((key, index) => {
          const isDone = index < activeIndex;
          const isActive = index === activeIndex;
          const isLast = index === STEP_KEYS.length - 1;

          return (
            <li key={key} className="flex items-stretch gap-3">
              {/* Status rail: glyph + connecting line that fills as steps complete */}
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors duration-500',
                    isDone && 'border-primary bg-primary text-primary-foreground',
                    isActive && 'border-primary text-primary',
                    !isDone && !isActive && 'border-border text-transparent',
                  )}
                >
                  {isDone ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  ) : (
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        isActive && 'bg-primary motion-safe:animate-pulse',
                        !isActive && 'bg-border',
                      )}
                    />
                  )}
                </span>
                {!isLast && (
                  <span
                    className={cn(
                      'my-1 w-px flex-1 transition-colors duration-500',
                      isDone ? 'bg-primary' : 'bg-border',
                    )}
                  />
                )}
              </div>

              <p
                className={cn(
                  'pb-5 pt-0.5 text-list-item-subtitle transition-colors duration-500',
                  isActive && 'text-foreground',
                  isDone && 'text-muted-foreground',
                  !isDone && !isActive && 'text-muted-foreground/50',
                )}
              >
                {t(key)}
              </p>
            </li>
          );
        })}
      </ol>

      <p className="text-center text-card-meta text-muted-foreground/70">
        {t('generatingAccuracyHint')}
      </p>
    </div>
  );
}
